import { bus } from "../core/bus";
import type { Objective, MissionSnapshot } from "./objectives";

export type MissionPhase = "intro" | "active" | "success" | "fail";

const INTRO_DURATION = 3.0; // seconds before mission goes live

export class Mission {
  private phase: MissionPhase = "intro";
  private introTimer = INTRO_DURATION;

  constructor(
    readonly id: string,
    readonly name: string,
    // Any win condition returning "success" ends the mission as a win (provided no fail)
    readonly winObjectives: Objective[],
    // Any fail condition returning "fail" ends the mission as a loss
    readonly failObjectives: Objective[],
  ) {}

  get currentPhase(): MissionPhase {
    return this.phase;
  }

  get isActive(): boolean {
    return this.phase === "active";
  }

  get isEnded(): boolean {
    return this.phase === "success" || this.phase === "fail";
  }

  start(): void {
    this.phase = "intro";
    this.introTimer = INTRO_DURATION;
  }

  // Immediate loss regardless of objectives — e.g. the aircraft going down.
  forceFail(): void {
    if (this.isEnded) return;
    this.phase = "fail";
    bus.emit("mission:ended", { success: false });
  }

  update(dt: number, snap: MissionSnapshot): void {
    if (this.phase === "intro") {
      this.introTimer -= dt;
      if (this.introTimer <= 0) {
        this.phase = "active";
        bus.emit("mission:started");
      }
      return;
    }
    if (this.phase !== "active") return;

    // Fail check first — a casualty threshold ends the mission immediately
    for (const obj of this.failObjectives) {
      if (obj.check(snap) === "fail") {
        this.phase = "fail";
        bus.emit("mission:ended", { success: false });
        return;
      }
    }

    // Win check — any primary objective achieved while no fail triggered
    for (const obj of this.winObjectives) {
      if (obj.check(snap) === "success") {
        this.phase = "success";
        bus.emit("mission:ended", { success: true });
        return;
      }
    }
  }

  objectiveStatuses(snap: MissionSnapshot): Array<{ label: string; status: string }> {
    return [
      ...this.winObjectives.map((o) => ({ label: o.label, status: o.check(snap) })),
      ...this.failObjectives.map((o) => {
        const s = o.check(snap);
        // Remap fail conditions: "pending" → tracking, "fail" → failed, shown as a constraint
        return { label: o.label, status: s === "fail" ? "fail" : "active" };
      }),
    ];
  }
}
